/**
 * The dialog's wiring: two or three reads, four answers on one surface, and the one call that ends
 * it.
 *
 * Decides nothing itself. Every rule it appears to apply lives in `model/` — `assignSurface`,
 * `fieldGate`, `assignmentRequest`, `assignProjection`, `assignBlocking`, `dropVerdict` — which is
 * why this file is state plus composition and can be read in one pass.
 *
 * ── There is no step machine any more, and that is the whole shape change ────────────────────
 * Variant D puts every question on one surface, so there is no position to hold, nothing can vanish
 * underneath the trainer, and `resolveStep`'s whole job is gone with `assignSteps.ts`. What the
 * step list was really encoding — which questions this plan HAS, and which can be answered yet — is
 * `assignForm.ts`, where a test can reach it without a renderer.
 *
 * ── Two entries, both from the builder ───────────────────────────────────────────────────────
 * A STORED plan opens on its row and asks who. An UNSAVED plan — one „Nowy plan" built for a client
 * from the client detail (23 September 2026) — opens already knowing who, carrying the builder's
 * tree: the store is read ONCE, when the dialog opens, and the tree is snapshotted as the exact
 * `draftToPostRequest` body the assignment will send. Everything after that — the projection, the
 * refusals, the POST — reads the snapshot, so the preview is the body. `AssignEntry` is the
 * difference, and the same state, projection and POST serve both.
 *
 * ── Times survive a mode change, and never reach the wire from the wrong mode ────────────────
 * A trainer who picks Stacjonarnie, sets the hours, switches to Zdalnie and changes their mind must
 * not lose them, so they stay in state. `assignmentRequest` is what drops them from the body, where
 * the mode is known and a test can reach the decision.
 *
 * ── `now` is captured once ───────────────────────────────────────────────────────────────────
 * The legal window is "today through today + 730 days". Reading the clock on every render would let
 * the window move underneath a dialog left open across midnight, so the flow's idea of today is
 * fixed when it opens — which is also what makes the picker's bounds stable.
 */

import { useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { forgetDraftKey } from '~/entities/ai-conversation';
import { clientDetailRoot, rosterQueryKey } from '~/entities/client';
import { assignableClients, matchingClients, type AssignableClient } from '~/entities/client';
import {
  DraftSerialisationError,
  bindAiDraft,
  draftToPostRequest,
  planLengthLabel,
  planLibraryQueryKey,
  planTemplateFullQueryOptions,
  readPlanDraft,
  readPlanRecipient,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import { ApiError } from '~/shared/api/mutator';
import { preferenceStore } from '~/shared/lib/preferenceStore';
import { getGetPlanAssignmentCustomerCustomerIdQueryKey } from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import type { TrainWithMeModulesTrainingDtosResponsesPlanAssignmentFullResponse as PlanAssignmentFullResponse } from '~/shared/api/generated/model';
import { parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';
import { dateTimeFormat } from '~/shared/lib/intlCache';
import { capitaliseFirst } from '~/shared/lib/localeCase';
import { browserTimeZone, sendRetryingWithoutZone } from '~/shared/lib/occurrenceZone';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { assignPlan, assignableClientsQueryOptions } from '../api/assignPlan';
import { assignBlocking } from '../model/assignBlocking';
import { assignBlockingMessage } from '../model/assignRefusalCopy';
import { assignConfirmation, type AssignConfirmation } from '../model/assignConfirmation';
import { assignFailureCopy } from '../model/assignFailure';
import {
  ASSIGN_FIELD_ORDER,
  assignSurface,
  fieldGate,
  type AssignEntry,
  type AssignField,
  type AssignSurface,
  type FieldGate,
} from '../model/assignForm';
import {
  ASSIGN_EXECUTION_MODES,
  DEFAULT_EXECUTION_MODE,
  assignDateWindow,
  assignmentRequest,
  isStationary,
  type AssignExecutionMode,
  type AssignSelection,
  type DateWindow,
  type OccurrenceOverride,
} from '../model/assignRequest';
import { buildScheduleCycles, flattenOccurrences } from '../model/assignSchedule';
import { assignTargetOf, unsavedAssignTargetOf, type AssignTarget } from '../model/assignTarget';
import { preStartDrops, type PreStartDrops } from '../model/preStartDrops';
import { applyDrop, dropVerdict, type DropVerdict } from '../model/projectionDrop';
import {
  applyOccurrenceEdit,
  occurrenceEditFields,
  restoreOccurrence,
  type OccurrenceEditFields,
  type OccurrenceEditRefusal,
} from '../model/occurrenceEdit';
import { assignProjection, type AssignProjection, type ProjectionChip } from '../model/projectionGrid';
import { selfPacedReview, type SelfPacedReview } from '../model/selfPacedReview';
import { defaultStationaryTimes, stationaryTimeSlots } from '../model/stationaryTimes';

/**
 * What the right-hand half is showing.
 *
 * One union rather than four null tests spread across the panel: "a self-paced review, or an
 * illustration, or a sentence, or the calendar" is a DECISION about which of four things the
 * trainer is looking at, and it was being re-derived in the component by asking `selfPaced === null
 * && projection !== null && projection.emptyMessage === null`. Two of those four reads have to
 * agree with each other for the panel to draw anything at all.
 */
export interface AssignPanel {
  /** "Kalendarz klienta" or "Kolejność sesji". */
  readonly title: string;
  readonly subtitle: string;
  /** Whether „Cały plan" has anything to say. A control that reveals nothing is not offered. */
  readonly expandable: boolean;
  readonly body: AssignPanelBody;
}

export type AssignPanelBody =
  /** A plan that books no dates: its sessions in order, and what unlocks the next one. */
  | { readonly kind: 'sequence'; readonly review: SelfPacedReview }
  /** Nothing to project yet — the illustration, and the sentence that says what is missing. */
  | { readonly kind: 'ambient'; readonly note: string }
  /** A plan that generates no sessions at all. */
  | { readonly kind: 'empty'; readonly message: string }
  | { readonly kind: 'calendar'; readonly projection: AssignProjection };

/** One Stationary slot the trainer fills. */
export interface TimeSlotView {
  readonly slot: number;
  /** "Poniedziałek · Push", or the fallback slot's own words. */
  readonly label: string;
  /** Said under the fallback slot only; `null` for a weekday. */
  readonly hint: string | null;
  readonly time: string;
}

/** One list's three drawn states, plus what it holds. */
export interface PickerView<Row> {
  readonly status: 'pending' | 'error' | 'ready';
  readonly rows: readonly Row[];
  /** Every row before the search — so "nothing at all" reads differently from "nothing matches". */
  readonly total: number;
  readonly query: string;
  readonly setQuery: (query: string) => void;
  readonly retry: () => void;
}

export interface AssignFlow {
  /** Which questions this assignment has. `pending` covers a read that has not landed. */
  readonly surface: AssignSurface;
  /**
   * `error` is the plan being unavailable — a stored plan's read failing, or an unsaved tree the
   * serialiser refused; `done` is the confirmation.
   */
  readonly status: 'loading' | 'error' | 'ready' | 'done';
  /** What the error state says, already worded. */
  readonly planError: string;
  /**
   * Ask for the plan again after a failed read. `null` where asking again cannot help — an unsaved
   * tree the serialiser refused is the same tree on a second try.
   */
  readonly retryPlan: (() => void) | null;

  /** The head's echo: the plan, its length and its system — and the client an unsaved plan is for. */
  readonly planEcho: string;
  readonly clientEcho: string | null;

  readonly gateOf: (field: AssignField) => FieldGate;
  readonly fields: readonly AssignField[];

  readonly clients: PickerView<AssignableClient>;
  readonly customerId: number | null;
  readonly customerName: string | null;
  readonly selectClient: (customerId: number) => void;

  readonly modes: readonly AssignExecutionMode[];
  readonly mode: AssignExecutionMode;
  readonly setMode: (mode: AssignExecutionMode) => void;

  readonly timeSlots: readonly TimeSlotView[];
  readonly setSlotTime: (slot: number, time: string) => void;

  /** `null` until the trainer picks one — variant D defaults no date. */
  readonly startDate: string | null;
  readonly window: DateWindow;
  readonly setStartDate: (iso: string) => void;
  /** Which ISO weekdays the plan trains on, for the picker's dots. */
  readonly programmedWeekdays: readonly number[];
  /** The withheld set, so the start field can say what a mid-week start costs. */
  readonly drops: PreStartDrops;

  /** Which of the four things the right-hand half is showing. */
  readonly panel: AssignPanel;
  /**
   * Every chip on the grid, by key — how the drag monitor resolves what was dropped. Empty on
   * every surface that draws no calendar.
   */
  readonly chips: ReadonlyMap<string, ProjectionChip>;
  readonly expanded: boolean;
  readonly toggleExpanded: () => void;

  readonly verdictFor: (chip: ProjectionChip, iso: string) => DropVerdict;
  readonly moveOccurrence: (chip: ProjectionChip, iso: string) => DropVerdict;
  readonly editFieldsFor: (chip: ProjectionChip) => OccurrenceEditFields;
  readonly editOccurrence: (
    chip: ProjectionChip,
    next: { readonly date: string; readonly time: string | null },
  ) => OccurrenceEditRefusal | null;
  readonly restoreOccurrence: (chip: ProjectionChip) => void;

  /** The one sentence beside the primary, and the field a refused press focuses. `null` when ready. */
  readonly blocking: { readonly reason: string; readonly field: AssignField | null } | null;
  readonly submitting: boolean;
  /** The server's refusal, already worded. `null` when nothing has failed. */
  readonly failure: string | null;
  readonly submit: () => void;
  /**
   * „Zapisz też jako szablon w bibliotece" — offered for an unsaved plan only, and `null` for a
   * stored library entry, which is in the library already.
   */
  readonly saveAsTemplate: { readonly checked: boolean; readonly set: (checked: boolean) => void } | null;

  /** The confirmation. Non-null exactly when `status` is `done`. */
  readonly confirmation: AssignConfirmation | null;
  /**
   * Start over on the same plan, for a second client — or `null` where there is no second client.
   *
   * An UNSAVED plan was built for one person, so "assign again" would resend it to the client who
   * has it, and `R14` answers that with a 409 every time. An act whose only outcome is a refusal is
   * not offered; a stored plan, which asks who each time, keeps it.
   */
  readonly assignAgain: (() => void) | null;
}

/** What the dialog opened on, or why it could not open on anything. */
type EntrySnapshot =
  | { readonly ok: true; readonly entry: AssignEntry }
  | { readonly ok: false; readonly message: string };

/**
 * Read the entry once, when the dialog opens: a stored plan by its id, an unsaved one from the store
 * — its tree serialised to the POST body right here, so the body cannot drift from what the trainer
 * reviews while the dialog is up.
 *
 * The serialiser can refuse. The gate keeps an empty phase from opening the dialog at all
 * (`unsavedAssignability`); the other refusals (`unidentifiedExercise`, and `ruleRefused`, which is
 * unreached today) are said in the builder's own save words, because they are the same refusal.
 */
function snapshotEntry(planId: number | null, t: ReturnType<typeof useTranslation>['t']): EntrySnapshot {
  if (planId !== null) return { ok: true, entry: { kind: 'stored', planId } };
  const draft = readPlanDraft();
  const recipient = readPlanRecipient();
  if (draft === null || recipient === null) {
    return { ok: false, message: t('web:assign.planError.message') };
  }
  try {
    return {
      ok: true,
      entry: { kind: 'unsaved', plan: draftToPostRequest(draft), origin: draft.origin, recipient },
    };
  } catch (error: unknown) {
    if (!(error instanceof DraftSerialisationError)) throw error;
    return {
      ok: false,
      message: t(`web:planBuilder.saveError.${error.reason}`, {
        name: error.nodeName,
        reason: error.refusal === null ? '' : t(error.refusal.key, error.refusal.values),
      }),
    };
  }
}

export function useAssignFlow(
  /** The stored plan, or `null` for the unsaved plan in the document store. */
  planId: number | null,
  /**
   * Called once an UNSAVED plan's assignment is written, with the client who now has it. The one
   * gate is here, on the entry: a stored plan's assignment never calls it.
   */
  onAssigned: ((customerId: number) => void) | undefined,
  /**
   * Raised the moment a send is fired and lowered when it settles — read by the dialog's root, which
   * sits above this hook and must answer a dismissal between the press and this hook's next render.
   */
  sendingRef: RefObject<boolean>,
): AssignFlow {
  const { t, i18n } = useTranslation();
  // The content language goes on the WIRE — it is what the backend resolves exercise names into.
  // Everything the reader sees worded, sorted or formatted stays on their own interface locale.
  const contentLang = useContentLanguage();
  const userId = useSessionUser()?.id ?? null;
  const locale = i18n.language;

  const [now] = useState(() => new Date());
  const [snapshot] = useState(() => snapshotEntry(planId, t));
  const entry = snapshot.ok ? snapshot.entry : null;
  const recipient = entry?.kind === 'unsaved' ? entry.recipient : null;
  const [chosenClient, setChosenClient] = useState<number | null>(recipient?.clientId ?? null);
  const [alsoSaveAsTemplate, setAlsoSaveAsTemplate] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [mode, setMode] = useState<AssignExecutionMode>(DEFAULT_EXECUTION_MODE);
  const [startDate, setStartDateState] = useState<Date | null>(null);
  // `null` until the trainer touches a slot: the seed depends on the plan, which has not loaded on
  // the first render, and an effect that seeded it later would overwrite a fast typist's first edit.
  const [times, setTimes] = useState<Record<number, string> | null>(null);
  const [overrides, setOverrides] = useState<ReadonlyMap<string, OccurrenceOverride>>(
    () => new Map<string, OccurrenceOverride>(),
  );
  const [expanded, setExpanded] = useState(false);
  const [result, setResult] = useState<PlanAssignmentFullResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const templateId = entry?.kind === 'stored' ? entry.planId : null;

  const plan = useQuery({
    ...planTemplateFullQueryOptions(templateId ?? 0, contentLang),
    enabled: templateId !== null,
  });
  // Asked only where there is a question: an unsaved plan opens already knowing its client.
  const roster = useQuery({ ...assignableClientsQueryOptions(), enabled: entry?.kind === 'stored' });
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // The zone retry wraps the request and nothing else: a body the server refused for the ZONE
    // alone goes again without it, reaching the profile-zone fallback every installed build uses.
    mutationFn: (body: Parameters<typeof assignPlan>[0]) =>
      sendRetryingWithoutZone(body, (sent) => assignPlan(sent, contentLang)),
    onSuccess: (assignment) => {
      setResult(assignment);
      setFailure(null);
      // A Buddy draft assigned straight to its client: the conversation is tied to the template
      // Buddy may still work on: the library entry „Zapisz też jako szablon" made, else the fork.
      // A client's own copy is recorded without a link (200, no template id), so no later read of
      // the conversation shows it bound: the draft key for this recipient is let go here.
      if (entry?.kind === 'unsaved') {
        const assignedFor = entry.recipient?.clientId ?? null;
        const origin = entry.origin;
        void bindAiDraft(origin, assignment.sourceTemplateId ?? assignment.planTemplateId).then((bound) => {
          // By its own key: a newer conversation that took this recipient's slot since is not it.
          if (bound && userId !== null && origin.kind === 'aiDraft' && origin.draftKey !== undefined) {
            forgetDraftKey(preferenceStore(), userId, assignedFor, origin.draftKey);
          }
        });
      }
      if (assignment.customerId != null) {
        // The client's assignment list — what the confirmation's own link opens onto.
        void queryClient.invalidateQueries({
          queryKey: getGetPlanAssignmentCustomerCustomerIdQueryKey(assignment.customerId),
        });
        // And the client detail, whose summary column draws the plan card this assignment fills.
        // An unsaved plan's assignment returns the trainer to that screen, and it would otherwise
        // still be showing "Zofia nie ma aktywnego planu".
        void queryClient.invalidateQueries({ queryKey: clientDetailRoot(assignment.customerId) });
      }
      void queryClient.invalidateQueries({ queryKey: rosterQueryKey });
      // The library's client counts, and — for „Zapisz też jako szablon" — a new entry in it.
      // R13: a copy counts toward the template it came from, a saved entry is one more row.
      void queryClient.invalidateQueries({ queryKey: planLibraryQueryKey });
      if (entry?.kind === 'unsaved' && assignment.customerId != null) onAssigned?.(assignment.customerId);
    },
    onError: (error: unknown) => {
      const problem = error instanceof ApiError ? error : null;
      // Key and values together — a key whose placeholder went unsupplied renders `{{plan}}` at the
      // trainer, so `model/` decides both. The problem body rides along whole because R14's refusal
      // names the plan already running, which is what makes the sentence actionable.
      const copy = assignFailureCopy(problem?.status ?? 0, problem?.code, problem?.problem);
      setFailure(t(copy.key, copy.values));
    },
    onSettled: () => {
      sendingRef.current = false;
    },
  });

  const target: AssignTarget | null =
    entry?.kind === 'unsaved'
      ? unsavedAssignTargetOf(entry.plan, entry.origin)
      : plan.data === undefined || templateId === null
        ? null
        : assignTargetOf(plan.data, templateId);
  const surface = assignSurface(target?.tree ?? null);
  const stationary = isStationary(mode);
  const window = assignDateWindow(now);

  const allClients = assignableClients(roster.data, locale);
  const customerName =
    recipient?.clientName ??
    allClients.find((client) => client.customerId === chosenClient)?.label ??
    null;

  const slots = target === null ? [] : stationaryTimeSlots(target.tree);
  const resolvedTimes = times ?? (target === null ? {} : defaultStationaryTimes(target.tree));
  const weekdayNames = dateTimeFormat(locale, { weekday: 'long' });
  const sessionNames = new Map(
    (target?.tree.phases ?? [])
      .flatMap((phase) => phase.days)
      .map((day) => [day.dayOfWeek ?? 0, day.name ?? day.sessions[0]?.name ?? ''] as const),
  );
  const timeSlots: TimeSlotView[] = slots.map((slot) => ({
    slot,
    label:
      slot === 0
        ? t('web:assign.times.fallbackLabel')
        : t('web:assign.times.slotLabel', {
            // 4 January 2027 is a Monday, so `3 + slot` lands on the weekday the slot names. A fixed
            // reference date rather than a hand-written table: the table would be a second source of
            // truth for weekday names `Intl` already answers.
            weekday: capitaliseFirst(weekdayNames.format(new Date(2027, 0, 3 + slot)), locale),
            session: sessionNames.get(slot) ?? '',
          }),
    hint: slot === 0 ? t('web:assign.times.fallbackHint') : null,
    time: resolvedTimes[slot] ?? '',
  }));

  const selection: AssignSelection = {
    customerId: chosenClient,
    startDate,
    mode,
    stationaryTimes: resolvedTimes,
    // Not read here. The zone is a fact about the browser at the moment of the WRITE, and this
    // selection is rebuilt on every keystroke in the search boxes; `submit` fills it in.
    zoneId: null,
    overrides,
    alsoSaveAsTemplate,
  };

  // Generated once and handed to everything that needs them: the request mapper checks the moved
  // coordinates against them, the start field counts what a mid-week start strikes, and the
  // projection draws them. A 52-cycle plan is 364 occurrences, and this runs on every keystroke in
  // the search boxes.
  const cycles =
    target === null || startDate === null || surface !== 'dated'
      ? []
      : buildScheduleCycles(target.tree, startDate);
  const occurrences = flattenOccurrences(cycles);
  const drops = preStartDrops(occurrences, overrides, startDate);

  const outcome = target === null ? null : assignmentRequest(target, selection, now, cycles);
  const refusals = outcome?.outcome === 'refused' ? outcome.refusals : [];

  const projection =
    surface !== 'dated' || startDate === null || target === null
      ? null
      : assignProjection({
          cycles,
          occurrences,
          drops,
          overrides,
          stationaryTimes: resolvedTimes,
          mode,
          startDate,
          expanded,
          locale,
          t,
        });
  const selfPaced =
    surface !== 'selfPaced' || target === null ? null : selfPacedReview({ tree: target.tree, locale, t });

  const calendarTitle = t('web:assign.live.titleCalendar');
  const expandable = projection !== null && (projection.truncated || expanded);
  const panel: AssignPanel =
    selfPaced !== null
      ? {
          title: t('web:assign.live.titleSequence'),
          subtitle: selfPaced.summary,
          expandable: false,
          body: { kind: 'sequence', review: selfPaced },
        }
      : projection === null
        ? {
            title: calendarTitle,
            subtitle: t('web:assign.live.awaiting'),
            expandable,
            body: {
              kind: 'ambient',
              note: t('web:assign.live.ambientCalendar'),
            },
          }
        : {
            title: calendarTitle,
            subtitle: projection.summary,
            expandable,
            body:
              projection.emptyMessage !== null
                ? { kind: 'empty', message: projection.emptyMessage }
                : { kind: 'calendar', projection },
          };

  const dropContext = { startDate, window };
  const editContext = { startDate, window, stationary };

  const statusOf = (): AssignFlow['status'] => {
    if (result !== null) return 'done';
    if (!snapshot.ok) return 'error';
    if (templateId !== null && plan.isPending) return 'loading';
    if (templateId !== null && target === null) return 'error';
    return 'ready';
  };

  const formState = {
    surface,
    knowsClient: recipient !== null,
    hasClient: chosenClient !== null,
    stationary,
    hasTimeSlots: slots.length > 0,
  };

  return {
    surface,
    status: statusOf(),
    planError: snapshot.ok ? t('web:assign.planError.message') : snapshot.message,
    retryPlan: snapshot.ok
      ? () => {
          void plan.refetch();
        }
      : null,

    planEcho:
      target === null
        ? t('web:assign.echo.loading')
        : t('web:assign.echo.plan', {
            name: target.name,
            length: planLengthLabel(
              surface === 'selfPaced' ? 'selfpaced' : 'date',
              cycleCountOf(target),
              t,
            ),
            system: t(
              surface === 'selfPaced'
                ? 'web:planCreate.system.selfPaced.title'
                : 'web:planCreate.system.dateBased.title',
            ),
          }),
    // No inflection: the echo says „dla: Zofia Żurawska" rather than declining the name, because a
    // Polish surname's genitive is not derivable and this app never guesses one.
    clientEcho: recipient === null ? null : t('web:assign.echo.client', { name: recipient.clientName }),

    gateOf: (field) => fieldGate(field, formState),
    fields: ASSIGN_FIELD_ORDER,

    clients: {
      status: roster.isPending ? 'pending' : roster.isError ? 'error' : 'ready',
      rows: matchingClients(allClients, clientQuery),
      total: allClients.length,
      query: clientQuery,
      setQuery: setClientQuery,
      retry: () => {
        void roster.refetch();
      },
    },
    customerId: chosenClient,
    customerName,
    selectClient: setChosenClient,

    modes: ASSIGN_EXECUTION_MODES,
    mode,
    setMode,

    timeSlots,
    setSlotTime: (slot, time) => {
      setTimes((current) => ({ ...(current ?? resolvedTimes), [slot]: time }));
    },

    startDate: startDate === null ? null : toIsoDate(startDate),
    window,
    setStartDate: (iso) => {
      const parsed = parseIsoDate(iso);
      if (parsed !== null) setStartDateState(parsed);
    },
    programmedWeekdays: programmedWeekdaysOf(target),
    drops,

    panel,
    chips: projection?.chipsByKey ?? EMPTY_CHIPS,
    expanded,
    toggleExpanded: () => {
      setExpanded((current) => !current);
    },

    verdictFor: (chip, iso) => dropVerdict(chip, iso, dropContext),
    moveOccurrence: (chip, iso) => {
      const verdict = dropVerdict(chip, iso, dropContext);
      if (verdict.ok) setOverrides((current) => applyDrop(current, chip, iso, dropContext));
      return verdict;
    },
    editFieldsFor: (chip) => occurrenceEditFields(chip, editContext),
    editOccurrence: (chip, next) => {
      const edited = applyOccurrenceEdit(overrides, chip, next, editContext);
      if (!edited.ok) return edited.refusal;
      setOverrides(edited.overrides);
      return null;
    },
    restoreOccurrence: (chip) => {
      setOverrides((current) => restoreOccurrence(current, chip));
    },

    blocking: blockingOf(assignBlocking(refusals, surface), locale, t),
    submitting: mutation.isPending,
    failure,
    submit: () => {
      if (outcome?.outcome !== 'ready' || target === null) return;
      setFailure(null);
      // Built once more, with the browser's zone in the selection. The zone is read HERE, at the
      // write: a value read during render is one the React Compiler may hoist, and it is a fact
      // about the browser at the moment of the write rather than about a render. Rebuilding rather
      // than patching the body keeps `assignmentRequest` the only thing that decides WHICH
      // assignments carry a zone. The call is pure and the refusals cannot differ.
      const sent = assignmentRequest(target, { ...selection, zoneId: browserTimeZone() }, now, cycles);
      if (sent.outcome !== 'ready') return;
      sendingRef.current = true;
      mutation.mutate(sent.body);
    },

    saveAsTemplate:
      entry?.kind === 'unsaved' ? { checked: alsoSaveAsTemplate, set: setAlsoSaveAsTemplate } : null,

    confirmation:
      result === null ? null : assignConfirmation(result, customerName, surface === 'selfPaced', t),
    assignAgain:
      recipient !== null
        ? null
        : () => {
            // The plan stays, everything about the PERSON goes. A second assignment that inherited
            // the first client's moved sessions would carry one trainer's repair onto another's
            // schedule.
            setResult(null);
            setFailure(null);
            setOverrides(new Map<string, OccurrenceOverride>());
            setStartDateState(null);
            setChosenClient(null);
            setExpanded(false);
          },
  };
}

/** The chip table a surface with no calendar hands to the drag monitor. Shared, so it is stable. */
const EMPTY_CHIPS: ReadonlyMap<string, ProjectionChip> = new Map<string, ProjectionChip>();

/** The footer's sentence and the field it sends the trainer to, or `null` when nothing blocks. */
function blockingOf(
  blocking: ReturnType<typeof assignBlocking>,
  locale: string,
  t: ReturnType<typeof useTranslation>['t'],
): AssignFlow['blocking'] {
  return blocking === null
    ? null
    : { reason: assignBlockingMessage(blocking, locale, t), field: blocking.field };
}

/** How many cycles this plan runs, across every phase. */
function cycleCountOf(target: AssignTarget): number {
  return target.tree.phases.reduce((total, phase) => total + phase.cycleCount, 0);
}

/**
 * The ISO weekdays the plan programmes, ascending — the dots under the month picker's days.
 *
 * A day declaring no weekday contributes none: it is placed on the START date's weekday, which is
 * the value the trainer is in the middle of choosing, so a dot for it would move as they hovered.
 */
function programmedWeekdaysOf(target: AssignTarget | null): number[] {
  const weekdays = new Set<number>();
  for (const phase of target?.tree.phases ?? []) {
    for (const day of phase.days) {
      if (day.sessions.length > 0 && day.dayOfWeek !== null) weekdays.add(day.dayOfWeek);
    }
  }
  return [...weekdays].sort((a, b) => a - b);
}
